/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

// components
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MembersSearch from './components/membersSearch'
import MembersTable from './components/membersTable'
import ViewMembers from './components/viewMembers'
import ConfirmationDialog from '../../../../components/confirmationDialog'

// helpers
import {
  makeEditGroupDTO,
  getSelectedMembersCount,
} from './addMembersModal.helpers'

// services
import { updateGroup } from '../../../../services/groups'

// hooks
import useLocalUsers from '../../../../hooks/useLocalUsers'
import useDialogVisibility from '../../../../hooks/useDialogVisibility'

import styles from './addMembersModal.module.scss'
import infoIcon from '../../../../assets/info-icon.svg'
import errorIcon from '../../../../assets/error-icon.svg'

const AddMembersModal = ({
  showModal,
  setShowModal,
  group,
  setGroup,
  getGroup,
}) => {
  const { t } = useTranslation()
  const [selectedMembers, setSelectedMembers] = useState(group?.members)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { users, areUsersLoading, getUsers } = useLocalUsers()
  const [
    isCancelConfirmationDialogOpen,
    openCancelConfirmationDialog,
    closeCancelConfirmationDialog,
  ] = useDialogVisibility(false)

  const handleClose = () => {
    const selectedMembersCount = getSelectedMembersCount(group)(selectedMembers)
    if (selectedMembersCount > 0) {
      openCancelConfirmationDialog()
    } else {
      setShowModal(false)
    }
  }

  const setAsSubmitted = () => setIsSubmitting(false)

  const handleUpdateGroupSuccess = (response) => {
    toast.success('Members updated successfully')
    setSelectedMembers([])
    /** Uncomment below code and remove the getGroup once we get email in patch group api */
    // const updatedGroup = {...group, members: response?.data?.members}
    // setGroup(updatedGroup)
    getGroup()
    setShowModal(false)
  }

  const handleUpdateGroupFailure = (error) => {
    toast.error(error?.message)
  }

  const handleAddMembers = () => {
    setIsSubmitting(true)
    const groupDTO = makeEditGroupDTO(group, selectedMembers)
    updateGroup(groupDTO, group?.id)
      .then(handleUpdateGroupSuccess)
      .catch(handleUpdateGroupFailure)
      .finally(setAsSubmitted)
  }

  useEffect(() => {
    if (showModal) {
      setSelectedMembers(group?.members)
    }
  }, [showModal])

  const handleSubmitCancelConfirmationDialog = () => {
    closeCancelConfirmationDialog()
    setShowModal(false)
  }

  return (
    <>
      <Dialog open={showModal} onClose={handleClose} maxWidth="xl">
        <DialogTitle className={styles.title}>
          <div className={styles.closeButtonContainer}>
            <Button
              className={styles.closeButton}
              disableRipple
              onClick={handleClose}
              endIcon={<CloseRoundedIcon className={styles.endIcon} />}
            >
              {t('edit-members-modal-close-button')}
            </Button>
          </div>
          <MembersSearch getUsers={getUsers} groupName={group?.displayName} />
          <ViewMembers
            selectedMembers={selectedMembers || []}
            setSelectedMembers={setSelectedMembers}
            getSelectedMembersCount={getSelectedMembersCount(group)}
            initialMembers={group?.members || []} 
          />
          <div className={styles.tipContainer}>
            <img
              src={infoIcon}
              alt=""
              style={{
                mt: -2.5,
                height: '25px',
                width: '25px',
                padding: '3px',
              }}
            />
            <Typography style={{ fontSize: '14px' }}>
              {t('edit-members-modal-limit-tip')}
            </Typography>
          </div>
          {getSelectedMembersCount(group)(selectedMembers) >= 5 && (
            <div className={styles.errorContainer}>
            <img
              src={errorIcon}
              alt=""
              style={{
                mt: -2.5,
                height: '25px',
                width: '25px',
                padding: '3px',
              }}
            />
            <Typography style={{ fontSize: '14px', fontWeight: '600' }}>
              {t('edit-members-modal-limit-error')}
            </Typography>
          </div>
          )}
        </DialogTitle>
        <DialogContent
          style={{
            overflow: 'hidden',
            marginTop: users.length === 0 ? '80px' : '0px'
          }}
        > 
          {users.length > 0 && (
            <Typography sx={{ fontSize: '12px', color: '#63666A' }}>
            {`${users.length} ${t('edit-members-modal-members-quantity')}`}
          </Typography>
          )}
          <MembersTable
            users={users}
            group={group}
            areUsersLoading={areUsersLoading}
            selectedMembers={selectedMembers}
            setSelectedMembers={setSelectedMembers}
            getSelectedMembersCount={getSelectedMembersCount(group)}
          />
        </DialogContent>
        {users.length > 0 && (
          <DialogActions
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingLeft: '24px',
            }}
          >
            <Button
              onClick={handleAddMembers}
              type="submit"
              className="primary-btn"
              disabled={isSubmitting || getSelectedMembersCount(group)(selectedMembers) === 0}
              startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
              sx={{
                marginTop: '20px',
                width: '162px',
                color: '#FFFFFF',
                fontSize: '1rem',
                backgroundColor: '#EB1700',
              }}
            >
              {!isSubmitting ? t('edit-members-modal-add-button') : ''}
            </Button>
            <Button
              onClick={handleClose}
              type="submit"
              className="secondary-btn"
              disabled={isSubmitting}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: '#6251371A',
                color: '#000000',
                fontSize: '1rem',
                width: '100px',
              }}
            >
              {t('cancel')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <ConfirmationDialog
        showDialog={isCancelConfirmationDialogOpen}
        onSubmit={handleSubmitCancelConfirmationDialog}
        onCancel={closeCancelConfirmationDialog}
      />
    </>
  )
}

export default AddMembersModal
